export type StepKey =
  | 'course'
  | 'sections'
  | 'automations'
  | 'profile'
  | 'branding'
  | 'team'
  | 'pco'
  | 'ministries'
  | 'preview'
  | 'billing'
  | 'publish'
  | 'share'
  | 'guests'
  | 'reports';

export type StepGroup = 'build' | 'setup' | 'launch';

export interface Step {
  key: StepKey;
  group: StepGroup;
  title: string;
  blurb: string;
  why: string;
  // Where the "Open this in the app" button takes the user. Computed at render
  // time when it needs runtime data (e.g. first course id).
  openLabel: string;
  // Path to a screenshot in /public/onboarding/ — drop a real one in here
  // later; the page renders gracefully if the file is missing.
  screenshot?: string;
  optional?: boolean;
}

// Order matters. The arc:
//
//   1. Build your class      — course, sections, automations
//   2. Set up your church    — profile, branding, team, pco, ministries
//   3. Launch & run it       — preview, billing, publish, share, guests, reports
//
// We lead with class-building because that's what the user actually came
// here to do — give them a real class to look at within the first few
// clicks, not a bureaucratic setup form. The "set up your church" group
// is then framed as "lock these in before you share with anyone" — at
// which point the work feels purposeful instead of busywork.
//
// Within each group the dependencies still flow forward: profile before
// share (URL slug ends up in every shared link), branding before preview
// (so screenshots look right), billing before publish (publish requires
// an active subscription).
export const steps: Step[] = [
  {
    blurb:
      'Pick a starter template (Starting Point, Membership, Spiritual Gifts) or start from scratch. A class is a guided sequence guests walk through on any device.',
    group: 'build',
    key: 'course',
    openLabel: 'Go to Class Directory',
    screenshot: '/onboarding/course.png',
    title: 'Create your first class',
    why: 'This is what you came here for. Everything else in OnArk hangs off a Class — sections, automations, the shareable link, your reports. Build something real in the first five minutes; we’ll polish the church setup right after.',
  },
  {
    blurb:
      'Inside a class, the left sidebar lists Sections — the building blocks. Click into any section to edit it. Section types: Check-in (how guests log in), Form (collect info), Text (instructions), Video (with optional prompts), Test (Enneagram / DISC / Spiritual Gifts), Branch (choose-your-own-adventure), Completion (the final page).',
    group: 'build',
    key: 'sections',
    openLabel: 'Open my first class',
    screenshot: '/onboarding/sections.png',
    title: 'Tour sections & the section sidebar',
    why: 'Most of the work building a class happens here. Knowing what each section type does is the difference between an OK class and a great one.',
  },
  {
    blurb:
      'On any class, open Automations. Add a rule that fires when a guest completes a section: send an email, set a PCO field, or trigger a PCO workflow. Conditions let you target rules (e.g. "only if they checked the Membership box").',
    group: 'build',
    key: 'automations',
    openLabel: 'Open Automations',
    screenshot: '/onboarding/automations.png',
    title: 'Add your first automation',
    why: 'Automations are where the magic lives. They turn a digital class into actual ministry follow-up — without a staff member touching anything.',
  },
  {
    blurb:
      'Confirm your church name, public URL slug, and timezone. The slug becomes part of every guest link.',
    group: 'setup',
    key: 'profile',
    openLabel: 'Open Church Settings',
    screenshot: '/onboarding/profile.png',
    title: 'Set your church profile',
    why: 'You’re about to share your class with real guests. Lock the URL slug in now — once it’s in a newsletter or text blast, changing it breaks every link you sent.',
  },
  {
    blurb:
      'Upload your logo and icon, pick your banner and button colors. Guests see your branding the moment they hit a class URL.',
    group: 'setup',
    key: 'branding',
    openLabel: 'Open Branding',
    screenshot: '/onboarding/branding.png',
    title: 'Set your branding',
    why: 'When you preview the class in the next group, you want it to already look like your church — not generic OnArk. Five minutes here pays off the moment a guest hits the link.',
  },
  {
    blurb:
      'Add other staff by email. They join as Editors, who can build and edit classes. You can hand one of them the Owner role later.',
    group: 'setup',
    key: 'team',
    openLabel: 'Open Team',
    screenshot: '/onboarding/team.png',
    title: 'Invite your team',
    why: 'You shouldn’t be the only one in here. Get the right people access so workflows don’t bottleneck on you.',
  },
  {
    blurb:
      'OAuth into your PCO account so guest data, custom fields, and workflows can flow between OnArk and PCO. Most of OnArk’s automations rely on this connection.',
    group: 'setup',
    key: 'pco',
    openLabel: 'Open Church Settings',
    optional: true,
    screenshot: '/onboarding/pco.png',
    title: 'Connect Planning Center',
    why: 'If you use PCO, this is the single highest-leverage step. Without it, automations like "add to workflow" or "set PCO field" can’t fire.',
  },
  {
    blurb:
      'If you use the Spiritual Gifts test or want guests routed to specific ministries based on test results, configure your ministries here.',
    group: 'setup',
    key: 'ministries',
    openLabel: 'Open Ministries',
    optional: true,
    screenshot: '/onboarding/ministries.png',
    title: 'Set up ministries (optional)',
    why: 'Only matters if you’re running test sections that recommend ministries. Skip if not.',
  },
  {
    blurb:
      'Open your class URL in a new tab (or your phone) and walk through it as a guest. This is the single best way to see what you actually built.',
    group: 'launch',
    key: 'preview',
    openLabel: 'Open my public class',
    screenshot: '/onboarding/preview.png',
    title: 'Preview as a guest',
    why: 'Bugs and confusing copy hide until you experience the class the way a guest will. Always do this before sharing the link.',
  },
  {
    blurb:
      'Choose the plan that fits your church size. Billing is metered by check-ins so you only pay for what gets used.',
    group: 'launch',
    key: 'billing',
    openLabel: 'Open Billing',
    screenshot: '/onboarding/billing.png',
    title: 'Pick a plan',
    why: 'An active subscription is required before you can publish a class. Pick a plan now so the next step (publish) is a single click.',
  },
  {
    blurb:
      'Until you click Publish, your edits stay in draft. Publishing pushes the current state of your class live so guests can take it.',
    group: 'launch',
    key: 'publish',
    openLabel: 'Open my class',
    title: 'Publish your class',
    why: 'Your guests only see the published version. Edits made after publishing stay invisible until you publish again — that way you can keep building without disturbing a class in progress.',
  },
  {
    blurb:
      'On any class page, click “Copy Link” at the top right. Paste that link into your church newsletter, sign-up form, or text message blast.',
    group: 'launch',
    key: 'share',
    openLabel: 'Open my class',
    title: 'Share the link',
    why: 'A class with no guests is just a draft. Get the link in front of the people you want to walk through it.',
  },
  {
    blurb:
      'Once guests start checking in, their info lands in the Guest Directory. Search, sort, filter by date range, and click any row to see that guest’s full history across all your classes.',
    group: 'launch',
    key: 'guests',
    openLabel: 'Open Guests',
    screenshot: '/onboarding/guests.png',
    title: 'See your guests',
    why: 'This is where the data lives. Knowing how to find a specific guest, see what they answered, and re-trigger automations is the difference between OnArk being a black box and being a tool you trust.',
  },
  {
    blurb:
      'Reports turns check-ins into numbers: how many guests, which automations fired, segment by condition. Export to CSV for staff meetings or to share with elders.',
    group: 'launch',
    key: 'reports',
    openLabel: 'Open Reports',
    screenshot: '/onboarding/reports.png',
    title: 'Read your reports',
    why: 'You need a way to tell the story — “50 people came through Starting Point this quarter, 28 joined a group” — without manually counting. Reports does that.',
  },
];

export const groupLabels: Record<StepGroup, string> = {
  build: 'Build your class',
  launch: 'Launch & run it',
  setup: 'Set up your church',
};

export interface StepHrefContext {
  firstCourseId?: string | null;
  firstCourseUrl?: string | null;
  churchUrl?: string | null;
}

export function getStepHref(key: StepKey, ctx: StepHrefContext = {}): string {
  const { firstCourseId } = ctx;

  // The class section editor (or the class list when no class exists yet).
  // `preview`/`publish`/`share` all land here so the spotlight tour can
  // highlight the toolbar buttons that live on that page — jumping straight
  // to the public URL made on-page guidance impossible.
  const courseEditor = firstCourseId
    ? `/admin/classes/${firstCourseId}`
    : '/admin/classes';

  const hrefs: Record<StepKey, string> = {
    automations: firstCourseId
      ? `/admin/classes/${firstCourseId}/automations`
      : '/admin/classes',
    billing: '/admin/billing',
    branding: '/admin/settings/branding',
    course: '/admin/classes',
    guests: '/admin/guests',
    ministries: '/admin/ministries',
    pco: '/admin/settings/church',
    preview: courseEditor,
    profile: '/admin/settings/church',
    publish: courseEditor,
    reports: '/admin/reports',
    sections: courseEditor,
    share: courseEditor,
    team: '/admin/team',
  };

  return hrefs[key];
}
